class YouMightAlsoLike extends HTMLElement {
  constructor() {
    super();
    this.getCrossSells = this.getCrossSells.bind(this);
    this.processCrossSells = this.processCrossSells.bind(this);
  }

  connectedCallback() {
    this.initializeElements();
  }

  initializeElements() {
    this.crossSellUpsellBox = this.querySelector(".cross-sell-upsell-box");
    this.crossSellContainer = this.querySelector(".cross-sell-container");
    if (this.crossSellUpsellBox) {
      this.title = this.crossSellUpsellBox.dataset.title;
      this.type = this.crossSellUpsellBox.dataset.type;
    }
    if (this.crossSellContainer) {
      this.render.bind(this)();
    }
  }

  /**
   * Fetches cross-sell items based on the product's title and type.
   * It constructs a query to fetch relevant products.
   *
   * @async
   * @param {string} title - The title of the current product, used to exclude it from cross-sell items and potentially extract color information.
   * @param {string} type - The type of the product, used to fetch related cross-sell items.
   * @returns {Array} crossSells - Array of cross-sell items.
   */
  getCrossSells = async () => {
    const query = `product_type:"${this.type}"`;
    const host = window.location.hostname;

    const res = await fetch(
      `https://${host}/search?view=dictionary&type=product&q=${query}+NOT+title:"${this.title.split("&")[0]}"`,
      { method: "GET" },
    );
    const response = await res.text();
    const data = response.split("let products = ")[1].split("</script>")[0];
    return JSON.parse(data);
  };

  /**
   * Processes cross-sell items based on color, handle any edge cases,
   * and shuffle them to randomize in carousel.
   *
   * @param {Array} crossSells - Array of cross-sell items to process.
   * @returns {Array} processedCrossSells - Array of processed cross-sell items.
   */
  processCrossSells = (crossSells) => {
    let processedCrossSells = crossSells;
    let color;
    if (this.title.indexOf("-") > 0) {
      color = this.title.split("-")[1].trim();
    } else {
      color = this.title.split(" ")[1].trim();
    }

    // Edge case with Rose Gold and Gold
    if (color === "Gold") {
      processedCrossSells.filter((el) => el.tags.includes("Gold"));
    }

    // Shuffle and filter
    processedCrossSells = this.constructor.shuffle(processedCrossSells);
    processedCrossSells = processedCrossSells.filter(
      (item) => item.title.split("- ")[1] === color,
    );

    return processedCrossSells;
  };

  /**
   * Shuffles an array. This function is a copy of "shuffle" from scripts.js.
   * Once scripts.js is converted to a module, we can remove it.
   *
   * @param {Array} array - The array to shuffle.
   * @returns {Array} array - The shuffled array.
   */
  static shuffle(array) {
    const shuffledArray = array;
    let currentIndex = array.length;
    let randomIndex;
    // While there remain elements to shuffle...
    while (currentIndex !== 0) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;

      // And swap it with the current element.
      [shuffledArray[currentIndex], shuffledArray[randomIndex]] = [
        shuffledArray[randomIndex],
        shuffledArray[currentIndex],
      ];
    }

    return shuffledArray;
  }

  /**
   * Builds the HTML content of the cross-sell carousel elements
   * with the provided cross-sell items by swapping in each carousel's innerHTML.
   * Limits the items to the first 12 cross-sells.
   *
   * @param {Array} crossSells - An array of cross-sell item objects to be displayed in the carousel.
   */
  buildHTMLContainer = (crossSells) => {
    const crossSellContainerHTML = crossSells
      .slice(0, 12)
      .map((item) => this.constructor.buildHTMLforItem(item))
      .join("");

    document.querySelectorAll(".cross-sell-carousel").forEach((carousel) => {
      // eslint-disable-next-line no-param-reassign
      carousel.innerHTML = crossSellContainerHTML;
    });
  };

  /**
   * Builds HTML for a single item, including picture, title, subtitle, and price.
   *
   * @param {Object} item - The cross-sell item object containing information such as title, images, price, etc.
   * @returns {string} HTML string for the cross-sell item to be injected into the DOM.
   */

  static buildHTMLforItem(item) {
    // Split the title by using the hyphen
    let [productTitle] = item.title.split("-");
    const productSubtitle = item.title.split("-").slice(1);
    let fabric = "";

    if (item.type !== "Swatch") {
      item.tags.find((tag) => {
        if (tag.toLowerCase().startsWith("fabric:")) {
          const { allFabrics } = window;
          const fabricTag = tag.split("fabric:")[1];
          fabric = allFabrics
            .find((windowTag) => windowTag.toLowerCase().includes(fabricTag))
            .trim();
        }
        return fabric;
      });
    }

    if (item.type === "Bridesmaid Dress") {
      productTitle = productTitle.replace(fabric, "");
    }

    const titleHtml = `<span class='product-card-title'>${productTitle}</span>`;
    const subtitleHtml = productSubtitle.length
      ? `<span class='product-card-subtitle font-normal'>${fabric} ${productSubtitle.join("-").replace(" in ", " ").trim()}</span>`
      : ``;

    // Return the HTML string for the individual item
    return `
        <div class="pointer carousel-item flex" style="scroll-snap-align:start;">
            <a class="no-underline carousel-link" data-carousel="PDP Upsell Carousel" href="${item.url}">
                <div>
                    <picture>
                        <source media="(max-width:480px)" srcset="${item.images[0].src.replace("1600x1600", "480x480")}">
                        <source media="(min-width:481px) and (max-width:768px)" srcset="${item.images[0].src.replace("1600x1600", "768x768")}">
                        <source media="(min-width:769px)" srcset="${item.images[0].src.replace("1600x1600", "900x900")}">
                        <img alt="${productTitle}" width="${item.images[0].width}" height="${item.images[0].height}" loading="lazy" class="upsell-carousel-image" style="object-fit:cover;" src="${item.images[0].src}">
                    </picture>
                    <div class="grid text-xs md:text-sm lh14 upsell-carousel-body" style="grid-auto-flow:column; justify-content:space-between; margin:.25rem 0;">
                        <div class="product-card-title flex flex-col text-left font-semibold">${titleHtml}${subtitleHtml}</div>
                        <div class="font-semibold">${
                          item.compare_at_price &&
                          Number(item.compare_at_price.slice(1)) <
                            Number(item.price.slice(1))
                            ? `<div style="color:#E3958A; text-decoration:line-through; text-decoration-thickness: 2px;"><span class="text-darkGrey">${item.price}</span></div><div style="color:#E3958A;">${item.compare_at_price}</div>`
                            : item.price
                        }</div>
                    </div>
                </div>
            </a>
        </div>
    `;
  }

  /**
   * Updates visibility of You Might Also Like based on whether or not items are present.
   */
  static updateVisibility() {
    const youMightAlsoLike = document.querySelectorAll(".you-might-also-like");
    const youMightAlsoLikeMobileItems = document.querySelectorAll(
      ".you-might-also-like-mobile .horizontal-carousel .cross-sell-carousel .carousel-item",
    );

    /**
     * showElements, hideElements, and loadItemsinGrid are copies from scripts.js.
     * Once scripts.js is converted to a module, we can remove them from here.
     *
     */

    function showElements(...elements) {
      elements.forEach((el) => {
        el?.classList.remove("none");
      });
    }

    function hideElements(...elements) {
      elements.forEach((el) => {
        el?.classList.add("none");
      });
    }

    function loadItemsInGrid(products, amountToLoad) {
      if (products) {
        for (let i = amountToLoad; i < products.length; i += 1) {
          hideElements(products[i]);
        }
      }
    }

    if (youMightAlsoLikeMobileItems.length) {
      showElements(...youMightAlsoLike);
      loadItemsInGrid(youMightAlsoLikeMobileItems, 4);
    } else {
      hideElements(...youMightAlsoLike);
    }
  }

  /**
   * Asynchronously fetches, processes, and renders cross-sell items based on the component's title and type.
   * This method handles the complete lifecycle for You Might Also Like.
   *
   * @async
   * @returns {Promise<void>} A promise that resolves when rendering process is complete. This method
   *                          updates the DOM or provides an error if it fails.
   */
  async render() {
    const crossSells = await this.getCrossSells();
    const processedCrossSells = this.processCrossSells(crossSells);
    this.buildHTMLContainer(processedCrossSells);
    this.constructor.updateVisibility();
  }
}

customElements.define("you-might-also-like", YouMightAlsoLike);
